import React, { useState, useRef, useEffect } from 'react';

const SNAPS = ['peek', 'half', 'full'];
// Share of the map's height per snap point — keep in step with .bottom-sheet.snap-* in the CSS
const SNAP_HEIGHT = { peek: 0.27, half: 0.55, full: 0.92 };
// Below this share of the lowest snap a release closes the sheet
const CLOSE_BELOW = 0.6;
// Movement under this is a tap, not a drag
const TAP_SLOP_PX = 8;
// A flick carries on for this long after the finger lets go when picking the snap point
const FLICK_PROJECT_MS = 180;

/**
 * Mobile container for the map's side panels.
 *
 * On a phone a right-hand drawer eats the map, and a full-screen modal takes it away
 * entirely — so the panels come up from the bottom with three snap points and the map stays
 * visible and usable above them. Above the breakpoint this renders nothing but its children,
 * so the desktop layout is untouched.
 *
 * The sheet follows the finger and settles on the snap point nearest to where a flick would
 * carry it — a fast swipe goes from peek to full in one go. It can be dragged by the grip
 * and by the content: a swipe up on a sheet that is not yet full grows the sheet before
 * anything scrolls, and a swipe down on content scrolled to its top shrinks or closes it.
 * It used to move only on release and only by the 22 px grip, which on a phone mostly meant
 * scrolling the content instead, or nothing at all.
 */
const BottomSheet = ({ open, children, onClose, initialSnap = 'half' }) => {
  const [snap, setSnap] = useState(initialSnap);
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const dragRef = useRef(null);
  // The native touch listeners outlive renders, so they read these instead of props
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) setSnap(initialSnap);
  }, [open, initialSnap]);

  const containerHeight = () => sheetRef.current?.parentElement?.clientHeight || window.innerHeight;

  const startDrag = (y) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    dragRef.current = {
      startY: y,
      startHeight: sheet.getBoundingClientRect().height,
      lastY: y,
      samples: [{ y, t: performance.now() }],
      moved: false,
      frame: null,
    };
    // Set on the element, not through state: it has to be gone again before the inline
    // height is dropped on release, or the sheet jumps to its snap instead of gliding there
    sheet.classList.add('dragging');
  };

  const moveDrag = (y) => {
    const d = dragRef.current;
    if (!d) return;
    d.lastY = y;
    if (Math.abs(y - d.startY) > TAP_SLOP_PX) d.moved = true;
    const now = performance.now();
    d.samples.push({ y, t: now });
    while (d.samples.length > 2 && now - d.samples[0].t > 100) d.samples.shift();
    // Straight onto the element, once per frame — a React render per touchmove is what
    // makes a sheet lag behind the finger
    if (d.frame == null) {
      d.frame = requestAnimationFrame(() => {
        d.frame = null;
        const max = containerHeight() * SNAP_HEIGHT.full;
        const height = Math.max(0, Math.min(max, d.startHeight - (d.lastY - d.startY)));
        if (sheetRef.current) sheetRef.current.style.height = `${height}px`;
      });
    }
  };

  const endDrag = ({ tapCycles }) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.frame != null) cancelAnimationFrame(d.frame);
    // The class takes over again and animates from wherever the finger left the sheet
    const sheet = sheetRef.current;
    if (sheet) {
      sheet.classList.remove('dragging');
      void sheet.offsetHeight; // commit the transition before the height changes
      sheet.style.height = '';
    }

    if (!d.moved) {
      if (tapCycles) {
        setSnap(SNAPS[(SNAPS.indexOf(snapRef.current) + 1) % SNAPS.length]);
      }
      return;
    }

    const first = d.samples[0];
    const last = d.samples[d.samples.length - 1];
    const dt = last.t - first.t;
    // px per ms, positive when the sheet grows
    const velocity = dt > 0 ? (first.y - last.y) / dt : 0;
    const total = containerHeight();
    const released = d.startHeight - (d.lastY - d.startY);
    const projected = released + velocity * FLICK_PROJECT_MS;

    if (projected < total * SNAP_HEIGHT.peek * CLOSE_BELOW) {
      onCloseRef.current?.();
      return;
    }
    const nearest = SNAPS.reduce((best, s) => (
      Math.abs(total * SNAP_HEIGHT[s] - projected) < Math.abs(total * SNAP_HEIGHT[best] - projected)
        ? s : best
    ));
    setSnap(nearest);
  };

  // ---- the content: native listeners, because only a non-passive touchmove can keep the
  // browser from scrolling once the gesture belongs to the sheet
  useEffect(() => {
    const body = bodyRef.current;
    if (!open || !body) return undefined;
    let gesture = null;

    const onTouchStart = (e) => {
      if (e.touches.length !== 1) { gesture = null; return; }
      const t = e.touches[0];
      gesture = { x0: t.clientX, y0: t.clientY, decided: false, active: false };
    };

    const onTouchMove = (e) => {
      if (!gesture) return;
      const t = e.touches[0];
      const dx = t.clientX - gesture.x0;
      const dy = t.clientY - gesture.y0;
      if (!gesture.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        gesture.decided = true;
        // Sideways belongs to whatever scrolls sideways in the panel, e.g. the tab row
        if (Math.abs(dx) > Math.abs(dy)) return;
        const atTop = body.scrollTop <= 0;
        gesture.active = (dy > 0 && atTop) || (dy < 0 && snapRef.current !== 'full');
        if (gesture.active) startDrag(gesture.y0);
      }
      if (gesture.active) {
        e.preventDefault();
        moveDrag(t.clientY);
      }
    };

    const onTouchEnd = () => {
      if (gesture?.active) endDrag({ tapCycles: false });
      gesture = null;
    };

    body.addEventListener('touchstart', onTouchStart, { passive: true });
    body.addEventListener('touchmove', onTouchMove, { passive: false });
    body.addEventListener('touchend', onTouchEnd);
    body.addEventListener('touchcancel', onTouchEnd);
    return () => {
      body.removeEventListener('touchstart', onTouchStart);
      body.removeEventListener('touchmove', onTouchMove);
      body.removeEventListener('touchend', onTouchEnd);
      body.removeEventListener('touchcancel', onTouchEnd);
    };
    // startDrag/moveDrag/endDrag only touch refs and stable setters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  // ---- the grip: pointer events, so it answers to a mouse as well as a finger
  const onPointerDown = (e) => {
    startDrag(e.clientY);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => { if (dragRef.current) moveDrag(e.clientY); };
  const onPointerUp = () => endDrag({ tapCycles: true });
  // The browser took the gesture over — settle where the sheet is rather than freeze mid-way
  const onPointerCancel = () => endDrag({ tapCycles: false });

  const cycle = () => setSnap(SNAPS[(SNAPS.indexOf(snap) + 1) % SNAPS.length]);

  return (
    <div ref={sheetRef} className={`bottom-sheet snap-${snap}`}>
      <div
        className="bottom-sheet-grip"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        role="button"
        tabIndex={0}
        aria-label="Panel-Höhe umschalten"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') cycle(); }}
      >
        <span className="bottom-sheet-bar" />
      </div>
      <div ref={bodyRef} className="bottom-sheet-body">{children}</div>
    </div>
  );
};

export default BottomSheet;
